import React, { useCallback, useContext, useEffect, useMemo } from "react";
import styled from "styled-components";

import "mapbox-gl/dist/mapbox-gl.css";
import { MapContext } from "../maps/MapContext";
import ReactMapGL, { FlyToInterpolator } from "react-map-gl";

import Box from "@material-ui/core/Box";
import Paper from "@material-ui/core/Paper";
import Branding from "../common/Branding";
import Container from "react-div-100vh";
import DamLogo from "./DamLogo";
import Header from "../common/Header";

import { MapboxContext } from "../maps/MapboxContext";

import DevelopmentsInfo from "./DevelopmentsInfo";
import Menu from "./Menu";
import MoreInfo from "./MoreInfo";
import Search from "./Search";
// import SROnly from './SROnly';
import Actions from "./Actions";
import Filters from "./Filters";
import { AppContext } from "./AppContext";

import { featureCollection } from "@turf/helpers";
import centroid from "@turf/centroid";

import Pin from "../common/Pin";
import NavigationControl from "../common/NavigationControl";
import { DEFAULT_VIEW_STATE, DEFAULT_VIEW_STATE_XS } from "./constants";

import { useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { Typography } from "@material-ui/core";

/**
 * READ THIS BEFORE STARTING:
 * There is a lot of 'prop-drilling' going on here. This
 * is because the app was built around the new hook useContext
 * which turned out to give a massive performance hit.
 *
 * All components using useContext(MapContext) were re-rendering
 * every time the map was panning. A better approach (when ready
 * for refactoring) is to split out state and dispatch into
 * separate contexts.
 *
 * see: https://kentcdodds.com/blog/how-to-optimize-your-context-value
 */

export default function App() {
  /**
   * restrict bounds and get viewState
   */
  const {
    setBounds,
    viewState,
    setViewStateInBounds,
    markers,
    setMarkers
  } = useContext(MapContext);
  useEffect(() => {
    setBounds([
      [144.9, -37.85], // Southwest coordinates
      [144.99, -37.78] // Northeast coordinates
    ]);
  }, [setBounds]);

  const { breakpoints } = useTheme();
  const isXs = useMediaQuery(breakpoints.only("xs"));
  useEffect(() => {
    let nextVs = { zoom: 14.5 };
    if (isXs) nextVs = DEFAULT_VIEW_STATE_XS;
    setViewStateInBounds(vs => ({
      ...vs,
      ...nextVs
    }));
    // eslint-disable-next-line
  }, [isXs]);

  const {
    onMapboxLoad,
    highlightedId,
    setHighlightedId,
    selectedId,
    setSelectedId,
    // setFilters,
    filters,
    data,
    mapRef
  } = useContext(MapboxContext);

  const [addressHover, setAddressHover] = React.useState();

  /**
   * Handle hover and send the permit_id
   * to the mapbox context for handling the
   * mapbox changes
   */
  const onHover = ({ features, center }) => {
    if (window.matchMedia("(any-pointer: coarse)").matches || !features) return;
    if (features.length === 0 && highlightedId !== undefined) {
      setHighlightedId();
      return;
    }

    const damFeatures = features.filter(d => d.source === "dam");
    if (damFeatures.length) {
      const { address, permit_id } = damFeatures[0].properties;
      setHighlightedId(permit_id);
      setAddressHover({ address, ...center });
    } else {
      setAddressHover();
    }
  };

  const flyToPolygon = feature => {
    const { geometry } = centroid(feature);
    const [longitude, latitude] = geometry.coordinates;
    setViewStateInBounds(vs => ({
      ...vs,
      latitude,
      longitude,
      transitionDuration: 350,
      transitionInterpolator: new FlyToInterpolator(),
      transitionEasing: t =>
        t < 0.5 ? 8 * t * t * t * t : 1 - 8 * --t * t * t * t,
      zoom: vs.zoom < 15.5 ? 15.5 : vs.zoom
    }));
  };

  /**
   * Handle click and send the permit_id to the
   * mapbox context to create a selected layer
   */
  const {
    activeFeatures,
    setActiveFeatures,
    contentScreen,
    setContentScreen,
    a11yMode,
    setA11yMode
  } = useContext(AppContext);
  const onClick = ({ features }) => {
    setAddressHover();
    if (features.length === 0) {
      setSelectedId();
      setActiveFeatures();
    }

    if (markers.length > 0) {
      setMarkers([]);
    }

    const damFeatures = features.filter(d => d.source === "dam");
    if (damFeatures.length) {
      setSelectedId(damFeatures[0].properties.permit_id);
      flyToPolygon(damFeatures[0]);

      /**
       * Sets the whole block as active features.
       */
      setTimeout(() => {
        const selectionIdFeatures = data.features.filter(
          d => d.properties.selection_id === features[0].properties.selection_id
        );
        setActiveFeatures(featureCollection(selectionIdFeatures));
      }, 351);

      /**
       * Track clicking on a building
       */
      window.gtag("event", "view_item", {
        event_label: damFeatures[0].properties.permit_id
      });
    }
  };

  const onPan = ({ isDragging }) => isDragging && setAddressHover();

  const branding = useMemo(
    () => (
      <Header>
        <Branding theme="dark">
          <DamLogo />
        </Branding>
      </Header>
    ),
    []
  );

  const goToHome = useCallback(() => {
    setActiveFeatures();
    setSelectedId();
    const vs = isXs ? DEFAULT_VIEW_STATE_XS : DEFAULT_VIEW_STATE;

    setViewStateInBounds(() => ({
      ...vs,
      transitionDuration: 500,
      transitionEasing: t =>
        t < 0.5 ? 8 * t * t * t * t : 1 - 8 * --t * t * t * t
    }));
  }, [isXs, setViewStateInBounds, setActiveFeatures, setSelectedId]);

  const toggleA11y = () => setA11yMode(a => !a);

  return (
    <>
      {/* <SROnly data={data} /> */}
      <Container>
        <Box display="flex" flexDirection="column" height="100%">
          {branding}
          <Box position="relative" flexGrow={1}>
            <Search
              setSelectedId={setSelectedId}
              setActiveFeatures={setActiveFeatures}
              setMarkers={setMarkers}
              setViewStateInBounds={setViewStateInBounds}
            />
            {/* <Helpers viewState={viewState} /> */}
            <Actions xs={isXs} goToHome={goToHome} toggleA11y={toggleA11y} />
            <MoreInfo
              contentScreen={contentScreen}
              setContentScreen={setContentScreen}
            />
            <Filters a11y={a11yMode} />
            <Menu />
            <DevelopmentsInfo
              activeFeatures={activeFeatures}
              selectedId={selectedId}
              setSelectedId={setSelectedId}
              flyTo={flyToPolygon}
              filters={filters}
            />
            <HeightHack>
              <ReactMapGL
                touchRotate={true}
                width="100%"
                height="100%"
                minZoom={13}
                maxZoom={17}
                mapboxApiAccessToken="pk.eyJ1IjoiZ2lzZmVlZGJhY2siLCJhIjoiY2pzeTNrcmxqMDhpbjQ0cGZ6d3pteng1cCJ9.BWEwpntKJgZWwu5qw3hpsA"
                mapStyle="mapbox://styles/gisfeedback/cjx2pbjdy541c1cmy67rqvw26"
                ref={ref => (mapRef.current = ref && ref.getMap())}
                onLoad={onMapboxLoad}
                onHover={onHover}
                onClick={onClick}
                onInteractionStateChange={onPan}
                onViewportChange={viewport =>
                  setViewStateInBounds(vs => ({ ...vs, ...viewport }))
                }
                interactiveLayerIds={["dam"]}
                {...viewState}
              >
                <NavigationControl />
                {markers.map((m, i) => (
                  <Pin key={i} {...m} />
                ))}
              </ReactMapGL>
            </HeightHack>
          </Box>
        </Box>
        {addressHover && (
          <Box
            position="absolute"
            zIndex="999"
            left={0}
            top={0}
            style={{
              transform: `translate3d(${addressHover.x +
                12}px, ${addressHover.y + 12}px, 0px)`
            }}
          >
            <Paper style={{ borderTopLeftRadius: 0 }}>
              <Box p={2}>
                <Typography variant="button">{addressHover.address}</Typography>
              </Box>
            </Paper>
          </Box>
        )}
      </Container>
    </>
  );
}

const HeightHack = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  top: 0;
  > div {
    height: 100% !important;
    width: 100% !important;
  }
`;
